import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Inbox, 
  CheckCircle2, 
  Clock, 
  AlertTriangle, 
  User,
  FileText,
  DollarSign,
  RefreshCw,
  Play,
  Pause,
  ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Queue definitions with metadata
const QUEUE_CONFIG = {
  AUTO_POST: { 
    label: 'Auto Post', 
    icon: CheckCircle2, 
    color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    description: 'Ready for automatic posting'
  },
  REVIEW_HIGH: { 
    label: 'High Priority Review', 
    icon: AlertTriangle, 
    color: 'bg-amber-50 text-amber-700 border-amber-200',
    description: 'High value or urgent items'
  },
  REVIEW: { 
    label: 'Standard Review', 
    icon: FileText, 
    color: 'bg-blue-50 text-blue-700 border-blue-200',
    description: 'Standard review queue'
  },
  APPEALS: { 
    label: 'Appeals', 
    icon: FileText, 
    color: 'bg-purple-50 text-purple-700 border-purple-200',
    description: 'Appealable denials'
  },
  MANUAL: { 
    label: 'Manual Entry', 
    icon: User, 
    color: 'bg-slate-50 text-slate-700 border-slate-200',
    description: 'Requires manual data entry'
  },
  EXCEPTIONS: { 
    label: 'Exceptions', 
    icon: AlertTriangle, 
    color: 'bg-red-50 text-red-700 border-red-200',
    description: 'Exception handling required'
  },
  PATIENT_BILLING: { 
    label: 'Patient Billing', 
    icon: DollarSign, 
    color: 'bg-teal-50 text-teal-700 border-teal-200',
    description: 'Patient responsibility items'
  }
};

function QueueSummaryCard({ queue, stats, onClick, isSelected }) {
  const config = QUEUE_CONFIG[queue] || { 
    label: queue, 
    icon: Inbox, 
    color: 'bg-muted text-foreground border-border',
    description: '' 
  };
  const Icon = config.icon;

  return (
    <Card 
      className={`cursor-pointer transition-all hover:shadow-md ${isSelected ? 'ring-2 ring-primary' : ''}`}
      onClick={onClick}
      data-testid={`queue-summary-${queue.toLowerCase()}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <Badge className={`${config.color} border`}>
            <Icon className="w-3 h-3 mr-1" />
            {config.label}
          </Badge>
          <span className="text-2xl font-bold">{stats?.total || 0}</span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Pending</span>
            <span className="font-medium">{stats?.pending || 0}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">In Progress</span>
            <span className="font-medium">{stats?.in_progress || 0}</span>
          </div>
          {stats?.overdue > 0 && (
            <div className="flex justify-between text-sm text-red-600">
              <span>Overdue</span>
              <span className="font-medium">{stats.overdue}</span>
            </div>
          )}
          {stats?.total_amount > 0 && (
            <div className="pt-2 border-t">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Amount</span>
                <span className="font-medium">${stats.total_amount.toLocaleString()}</span>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function WorkItemsTable({ queueName }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['queueItems', queueName, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      const res = await fetch(`${API_URL}/api/route/queues/${queueName}?${params}`);
      if (!res.ok) throw new Error('Failed to fetch queue items');
      return res.json();
    },
    enabled: !!queueName,
    refetchInterval: 10000
  });

  const assignMutation = useMutation({
    mutationFn: async ({ itemId, userId }) => {
      const res = await fetch(`${API_URL}/api/route/work-items/${itemId}/assign?user_id=${userId}`, {
        method: 'POST'
      });
      if (!res.ok) throw new Error('Failed to assign item');
      return res.json();
    },
    onSuccess: () => {
      toast.success('Item assigned successfully');
      queryClient.invalidateQueries(['queueItems']);
    }
  });

  const completeMutation = useMutation({
    mutationFn: async ({ itemId }) => {
      const res = await fetch(`${API_URL}/api/route/work-items/${itemId}/complete`, {
        method: 'POST'
      });
      if (!res.ok) throw new Error('Failed to complete item');
      return res.json();
    },
    onSuccess: () => {
      toast.success('Item marked as complete');
      queryClient.invalidateQueries(['queueItems']);
      queryClient.invalidateQueries(['routingStats']);
    }
  });

  const items = data?.items || [];
  const queueConfig = QUEUE_CONFIG[queueName] || { label: queueName };

  const getSLABadge = (item) => {
    if (!item.due_date) return null;
    
    const now = new Date();
    const due = new Date(item.due_date);
    const hoursRemaining = (due - now) / (1000 * 60 * 60);
    
    if (hoursRemaining < 0) {
      return <Badge variant="destructive" className="text-xs">Overdue</Badge>;
    } else if (hoursRemaining < item.sla_hours * 0.25) {
      return <Badge className="bg-amber-100 text-amber-700 text-xs">At Risk</Badge>;
    }
    return <Badge className="bg-emerald-100 text-emerald-700 text-xs">On Time</Badge>;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              {queueConfig.label}
              <Badge variant="secondary">{items.length} items</Badge>
            </CardTitle>
            <CardDescription>{queueConfig.description}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value=" ">All Status</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                <SelectItem value="ON_HOLD">On Hold</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-16" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Inbox className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No items in this queue</p>
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Document</TableHead>
                  <TableHead>Payer</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>SLA</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id || item._id} data-testid="work-item-row">
                    <TableCell>
                      <div 
                        className="flex items-center space-x-2 cursor-pointer hover:text-primary"
                        onClick={() => navigate(`/documents/${item.document_id}`)}
                      >
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium truncate max-w-[200px]">
                          {item.document_filename || 'Document'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {item.payer_name || '-'}
                    </TableCell>
                    <TableCell className="text-sm font-medium">
                      {item.total_amount > 0 ? `$${item.total_amount.toLocaleString()}` : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={item.priority} className="w-12 h-1.5" />
                        <span className="text-xs font-mono">{item.priority}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {getSLABadge(item)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {item.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {item.status === 'PENDING' && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => assignMutation.mutate({ itemId: item.id || item._id, userId: 'current_user' })}
                            title="Start working"
                          >
                            <Play className="w-4 h-4" />
                          </Button>
                        )}
                        {item.status === 'IN_PROGRESS' && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => completeMutation.mutate({ itemId: item.id || item._id })}
                            title="Mark complete"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => navigate(`/documents/${item.document_id}`)}
                          title="View document"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function QueuesPage() {
  const [selectedQueue, setSelectedQueue] = useState('MANUAL');

  // Fetch all queue stats
  const { data: queuesData, isLoading } = useQuery({
    queryKey: ['allQueues'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/route/queues`);
      if (!res.ok) throw new Error('Failed to fetch queues');
      return res.json();
    },
    refetchInterval: 10000
  });

  // Fetch routing stats
  const { data: routingStats } = useQuery({
    queryKey: ['routingStats'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/route/stats`);
      if (!res.ok) throw new Error('Failed to fetch routing stats');
      return res.json();
    },
    refetchInterval: 10000
  });

  const queues = queuesData?.queues || [];
  const queueStatsMap = queues.reduce((acc, q) => {
    acc[q.queue] = q;
    return acc;
  }, {});

  // Define queue order
  const queueOrder = ['REVIEW_HIGH', 'APPEALS', 'EXCEPTIONS', 'MANUAL', 'REVIEW', 'AUTO_POST', 'PATIENT_BILLING'];

  if (isLoading) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          <Skeleton className="h-10 w-48" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-testid="queues-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground font-heading">Work Queues</h1>
          <p className="text-muted-foreground mt-1">Manage and process routed work items</p>
        </div>
        <div className="flex items-center gap-4 mt-4 sm:mt-0">
          {/* SLA Summary */}
          <div className="flex items-center gap-3 text-sm">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              <span>{routingStats?.sla?.on_time || 0} On Time</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-amber-500" />
              <span>{routingStats?.sla?.at_risk || 0} At Risk</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-red-500" />
              <span>{routingStats?.sla?.overdue || 0} Overdue</span>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs">Total Work Items</CardDescription>
            <CardTitle className="text-2xl">{routingStats?.total_items || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs">Pending</CardDescription>
            <CardTitle className="text-2xl text-amber-600">{routingStats?.pending || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs">In Progress</CardDescription>
            <CardTitle className="text-2xl text-blue-600">{routingStats?.in_progress || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs">Completed Today</CardDescription>
            <CardTitle className="text-2xl text-emerald-600">{routingStats?.completed_today || 0}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Queue Cards */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Queues</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {queueOrder.map(queue => (
            <QueueSummaryCard
              key={queue}
              queue={queue}
              stats={queueStatsMap[queue]}
              onClick={() => setSelectedQueue(queue)}
              isSelected={selectedQueue === queue}
            />
          ))}
        </div>
      </div>

      {/* Selected Queue Items */}
      {selectedQueue && (
        <WorkItemsTable queueName={selectedQueue} />
      )}
    </div>
  );
}
